import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { authService } from '../services/authService';
import { tokenStorage } from '../services/tokenStorage';
import { deviceManager } from '../services/deviceManager';
import { api } from '../services/api';

// Auth state management
const initialState = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
  menus: [],
  menusLoaded: false,       // true once the menus API call has settled
  deviceId: null,
  deviceAuthorized: false,
  sessionTimeout: false
};

// Action types
const AUTH_ACTIONS = {
  LOGIN_START: 'LOGIN_START',
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGIN_FAILURE: 'LOGIN_FAILURE',
  LOGOUT: 'LOGOUT',
  SET_USER: 'SET_USER',
  SET_LOADING: 'SET_LOADING',
  CLEAR_ERROR: 'CLEAR_ERROR',
  SET_MENUS: 'SET_MENUS',
  SET_DEVICE_ID: 'SET_DEVICE_ID',
  SET_DEVICE_AUTHORIZED: 'SET_DEVICE_AUTHORIZED',
  DEVICE_NOT_ALLOWED: 'DEVICE_NOT_ALLOWED',
  SESSION_TIMEOUT: 'SESSION_TIMEOUT',
  SET_MENUS_LOADED: 'SET_MENUS_LOADED'
};

// Reducer function
function authReducer(state, action) {
  switch (action.type) {
    case AUTH_ACTIONS.LOGIN_START:
      return {
        ...state,
        isLoading: true,
        error: null,
        sessionTimeout: false
      };
    case AUTH_ACTIONS.LOGIN_SUCCESS:
      return {
        ...state,
        user: action.payload.user,
        isAuthenticated: true,
        isLoading: false,
        error: null,
        deviceId: action.payload.deviceId,
        deviceAuthorized: true,
        sessionTimeout: false
      };
    case AUTH_ACTIONS.LOGIN_FAILURE:
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload.error,
        deviceAuthorized: false,
        sessionTimeout: false
      };
    case AUTH_ACTIONS.LOGOUT:
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
        menus: [],
        menusLoaded: false,
        deviceAuthorized: false,
        sessionTimeout: false
      };
    case AUTH_ACTIONS.SET_MENUS:
      return {
        ...state,
        menus: action.payload || []
      };
    case AUTH_ACTIONS.SET_MENUS_LOADED:
      return {
        ...state,
        menusLoaded: action.payload
      };
    case AUTH_ACTIONS.SET_USER:
      return {
        ...state,
        user: action.payload.user,
        isAuthenticated: true,
        isLoading: false
      };
    case AUTH_ACTIONS.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload
      };
    case AUTH_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null
      };
    case AUTH_ACTIONS.SET_DEVICE_ID:
      return {
        ...state,
        deviceId: action.payload
      };
    case AUTH_ACTIONS.SET_DEVICE_AUTHORIZED:
      return {
        ...state,
        deviceAuthorized: action.payload
      };
    case AUTH_ACTIONS.DEVICE_NOT_ALLOWED:
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: 'Login is restricted to your registered device. Please use the same device or contact support.',
        deviceAuthorized: false
      };
    case AUTH_ACTIONS.SESSION_TIMEOUT:
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: 'Your session has expired. Please log in again.',
        sessionTimeout: true,
        deviceAuthorized: false
      };
    default:
      return state;
  }
}

// Create context
const AuthContext = createContext(null);

// Auth provider component
export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Helper to load menus for current user.
  // Always dispatches SET_MENUS_LOADED: true when the call settles (success or error)
  // so ProtectedRoute never waits forever.
  const loadMenus = async () => {
    try {
      const res = await api.get('menus/my');
      dispatch({ type: AUTH_ACTIONS.SET_MENUS, payload: Array.isArray(res.data) ? res.data : [] });
    } catch (err) {
      dispatch({ type: AUTH_ACTIONS.SET_MENUS, payload: [] });
    } finally {
      dispatch({ type: AUTH_ACTIONS.SET_MENUS_LOADED, payload: true });
    }
  };

  // Check for existing token on app load
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // Initialize device ID on app load
        const deviceId = deviceManager.getDeviceId();
        dispatch({ type: AUTH_ACTIONS.SET_DEVICE_ID, payload: deviceId });

        const token = tokenStorage.getAccessToken();
        if (token) {
          // Try to get cached user info first
          let user = tokenStorage.getUser();
          // If no cached user, attempt to fetch from server
          if (!user) {
            try {
              
              user = await authService.getCurrentUser();
            } catch (error) {
              console.error('Failed to fetch current user, clearing session:', error);
              tokenStorage.clearTokens();
              dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
              return;
            }
          }

          dispatch({
            type: AUTH_ACTIONS.SET_USER,
            payload: { user }
          });
          // Set device as authorized since we have valid token and device ID
          dispatch({ type: AUTH_ACTIONS.SET_DEVICE_AUTHORIZED, payload: true });
          // load menus for the user
          await loadMenus();
        } else {
          dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        tokenStorage.clearTokens();
        dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
      }
    };

    initializeAuth();
  }, []); // <-- Empty dependency array ensures this runs only once on mount

  // Login function
  const login = async (email, password) => {
    dispatch({ type: AUTH_ACTIONS.LOGIN_START });
    
    try {
      // Initialize or retrieve device ID
      const deviceId = deviceManager.getDeviceId();
      dispatch({ type: AUTH_ACTIONS.SET_DEVICE_ID, payload: deviceId });
      
      // Perform login with device ID
      const response = await authService.login(email, password, deviceId);
      // Extract user info from login response (already included)
      const user = response.user;     
      
      // Store tokens and user info
      tokenStorage.setTokens(response.access_token, response.refresh_token, user);
      //user = await authService.getCurrentUser();
      dispatch({
        type: AUTH_ACTIONS.LOGIN_SUCCESS,
        payload: { user, deviceId }
      });

      // load menus for this user
      await loadMenus();
      
      return { success: true };
    } catch (error) {
      // Handle device not allowed error specifically
      if (error.response?.status === 403 && error.response?.data?.code === 'DEVICE_NOT_ALLOWED') {
        dispatch({ type: AUTH_ACTIONS.DEVICE_NOT_ALLOWED });
        return { 
          success: false, 
          error: 'Login is restricted to your registered device. Please use the same device or contact support.' 
        };
      }
      
      const errorMessage = error.response?.data?.detail || error.response?.data?.message || 'Login failed';
      dispatch({
        type: AUTH_ACTIONS.LOGIN_FAILURE,
        payload: { error: errorMessage }
      });
      return { success: false, error: errorMessage };
    }
  };

  // Logout function
  const logout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      tokenStorage.clearTokens();
      // Do not clear device ID on logout - let backend policy handle device reset
      dispatch({ type: AUTH_ACTIONS.LOGOUT });
    }
  };

  // Register function
  const register = async (email, password) => {
    dispatch({ type: AUTH_ACTIONS.LOGIN_START });
    
    try {
      await authService.register(email, password);
      
      // Auto-login after registration
      const loginResult = await login(email, password);
      return loginResult;
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Registration failed';
      dispatch({
        type: AUTH_ACTIONS.LOGIN_FAILURE,
        payload: { error: errorMessage }
      });
      return { success: false, error: errorMessage };
    }
  };

  // Clear error function
  const clearError = () => {
    dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });
  };

  // Helper to check if a path is authorized (menu items expected to have `path`)
  const isAuthorized = (pathname = '') => {
    const normalize = (p) => {
      if (!p) return '';
      const s = p.toString().trim();
      return s.startsWith('/') ? s : '/' + s;
    };
    const target = normalize(pathname);
    const menus = state.menus || [];

    const checkItem = (item) => {
      const mp = normalize(item.path || item.link || item.url || item.route || '');
      if (!mp) return false;
      if (mp === target) return true;
      if (target.startsWith(mp + '/')) return true; // allow nested routes
      if (item.children && item.children.length) {
        return item.children.some(checkItem);
      }
      return false;
    };

    return menus.some(checkItem);
  };

  const value = {
    ...state,
    login,
    logout,
    register,
    clearError,
    isAuthorized
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

// Custom hook to use auth context
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
